import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";
import { Link } from "react-router-dom";
import {
  Field,
  FieldArray,
  reduxForm,
  SubmissionError,
  change,
  formValueSelector,
} from "redux-form";
import { fetchCustomers } from "../actions/customers";
import ErrorMessage from "../components/ErrorMessage";
import LoadingProgress from "../components/LoadingProgress";

import Box from "../components/Box";
import ToggleSwitch from "../components/ToggleSwitch";
import "../styles/MachinesAdminView.css";
import { fetchQR, updateQR } from "../actions/qrs";
import {
  renderInputField,
  renderTextAreaField,
  renderSelectField,
  renderColorField,
} from "../utils/forms";
import QRZonesList from "../components/QRZonesList";
import HeaderPreview from "../components/HeaderPreview";
import DesktopPreview from "../components/DesktopPreview";
import SmartphonePreview from "../components/SmartphonePreview";

const renderTimeSlots = ({ fields, meta: { error } }) => (
  <div>
    {fields.map((slot, index) => (
      <div key={index} className="d-flex align-items-center mb-2">
        <Field
          name={slot}
          component="input"
          type="number"
          className="form-control mr-2"
          parse={value => value ? Number(value) : null}
        />
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={() => fields.remove(index)}
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>
    ))}
    <button
      type="button"
      className="btn btn-sm bg-vw-light text-white mt-2"
      onClick={() => fields.push(60)}
    >
      <i className="fas fa-plus mr-1"></i>Añadir franja
    </button>
    {error && <div className="text-danger mt-2">{error}</div>}
  </div>
);

class QREditForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      customerSelected: "",
      showDynamicField: false,
      activeTab: "basic", // Add active tab state
      requireIdNumber: false,
      useRedsysInsite: false,
    };

    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    const { fetchQR, fetchCustomers, match } = this.props;

    fetchQR(match.params.id);
    fetchCustomers();

    this.load(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.load(nextProps);
  }

  load(props) {
    const { qr, customers } = props;

    if (!this.state.loaded && qr && customers) {
      this.setState({
        loaded: true,
        customerSelected: qr.Customer ? (qr.Customer._id || qr.Customer) : "",
        requireIdNumber: qr.requireIdNumber || false,
        useRedsysInsite: qr.useRedsysInsite || false,
      });
    }
  }

  submit(props) {
    const { updateQR, qr } = this.props;

    return updateQR(qr._id, props).then((response) => {
      if (response.error) {
        throw new SubmissionError({
          _error: response.error.message,
          ...response.error.fields,
        });
      }

      return response;
    });
  }

  handleShowDynamicField() {
    this.setState({
      showDynamicField: true,
    });
  }

  handleFileUpload(e) {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        alert("El archivo es demasiado grande. El tamaño máximo es 2MB.");
        e.target.value = ""; // Clear the input
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Create canvas to compress image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set max dimensions to reduce size
          const maxWidth = 300;
          const maxHeight = 300;
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          
          // Clear canvas with transparency
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          // Try PNG first (for transparency), then JPEG if too large
          let compressedDataUrl = canvas.toDataURL('image/png');
          
          // If PNG is too large (>2MB base64), try JPEG with white background
          const maxBase64Size = 2 * 1024 * 1024; // 2MB
          if (compressedDataUrl.length > maxBase64Size) {
            // Try JPEG with different quality levels
            let quality = 0.8;
            
            // Fill white background for JPEG
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, width, height);
            
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            
            // Reduce quality if still too large
            while (compressedDataUrl.length > maxBase64Size && quality > 0.3) {
              quality -= 0.1;
              compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            }
          }

          if (compressedDataUrl.length > maxBase64Size * 1.2) {
            alert("La imagen es demasiado grande incluso después de la compresión. Por favor, use una imagen más pequeña o simple.");
            e.target.value = "";
            return;
          }

          this.props.dispatch(
            change("QREditForm", "headerConfig.logoBase64", compressedDataUrl)
          );
        };
        img.onerror = () => {
          alert("Error al cargar la imagen. Por favor, intente con otra imagen.");
          e.target.value = "";
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  render() {
    const {
      customers,
      qr,
      error,
      pristine,
      submitting,
      handleSubmit,
      primaryColor,
      secondaryColor,
      backgroundColor,
      textPrimary,
      textSecondary,
      logoBase64,
      backgroundImage,
      backgroundSmartphoneImage,
      languageSelectorColor,
      buttonColor1,
      buttonColor2,
      buttonText,
      iconsColor,
    } = this.props;
    const { showDynamicField, loaded, activeTab } = this.state;

    if (!loaded) {
      return <LoadingProgress />
    }

    if (!customers) {
      return <LoadingProgress />
    }

    return (
      <div className="machines-admin-view">
      <form onSubmit={handleSubmit(this.submit)}>
        <div className="mach-admin-header">
          <div className="header-content">
            <div className="header-text">
              <h1>
                <i className="fas fa-qrcode mr-2"></i>
                Editar QR
              </h1>
            </div>
            <div className="header-actions">
              <Link to={`/qrs/${qr._id}`} className="mach-admin-btn secondary">
                <i className="fas fa-arrow-left"></i>
                <span className="d-none d-sm-inline">Volver</span>
              </Link>
              <button
                className="mach-admin-btn success"
                type="submit"
                disabled={pristine || submitting}
              >
                <i className="fas fa-save"></i>
                <span className="d-none d-sm-inline">{submitting ? 'Guardando...' : 'Guardar'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Left Column - Tabs and Form Fields */}
          <div className="col-12 col-lg-7">
            {/* Tab Navigation */}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "basic" ? "active" : ""}`}
                  onClick={() => this.setState({ activeTab: "basic" })}
                >
                  <i className="fas fa-cubes mr-1"></i>
                  Configuración Básica
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "header" ? "active" : ""}`}
                  onClick={() => this.setState({ activeTab: "header" })}
                >
                  <i className="fas fa-paint-brush mr-1"></i>
                  Header
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "rental" ? "active" : ""}`}
                  onClick={() => this.setState({ activeTab: "rental" })}
                >
                  <i className="fas fa-palette mr-1"></i>
                  Personalización Rental
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "images" ? "active" : ""}`}
                  onClick={() => this.setState({ activeTab: "images" })}
                >
                  <i className="fas fa-image mr-1"></i>
                  Imágenes
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "time" ? "active" : ""}`}
                  onClick={() => this.setState({ activeTab: "time" })}
                >
                  <i className="fas fa-clock mr-1"></i>
                  Tiempo
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
              {/* Basic Configuration Tab */}
              {activeTab === "basic" && (
                <div className="tab-pane active">
                  <Box title="Configuración Básica" icon="cubes">
                    <QRZonesList customers={customers} customerSelected={this.state.customerSelected} />
                    <Field name="Type" component={renderSelectField} label="Tipo">
                      <option value="">Selecciona un tipo...</option>
                      <option value="pay">Pago</option>
                      <option value="free">Gratis</option>
                      <option value="test">Test</option>
                    </Field>
                    <Field name="Mode" component={renderSelectField} label="Modo">
                      <option value="">Selecciona un modo...</option>
                      <option value="0">MTO DIARIO</option>
                      <option value="4">RESERVA ANTICIPADA</option>
                      <option value="1">OTO DIARIO SIN PIN</option>
                      <option value="3">OTO DIARIO CON PIN</option>
                      <option value="5">TIEMPO SIN PIN</option>
                      <option value="2">BAQUEIRA</option>
                    </Field>
                    <div className="row" style={{ alignItems: 'center', marginTop: '10px' }}>
                      <div className="col-md-6">
                        <Field
                          name="requireIdNumber"
                          component={ToggleSwitch}
                          label="Solicitar Documento de Identidad"
                          onChange={(e, newValue) => this.setState({ requireIdNumber: newValue })}
                        />
                      </div>
                      <div className="col-md-6">
                        {this.state.requireIdNumber ? (
                          <div className="alert alert-success" style={{ fontSize: '12px', margin: 0 }}>
                            <i className="fas fa-check-circle mr-1"></i>
                            <strong>Activo:</strong> Se pedirá el DNI / NIE / Pasaporte al cliente.
                          </div>
                        ) : (
                          <div className="alert alert-warning" style={{ fontSize: '12px', margin: 0 }}>
                            <i className="fas fa-info-circle mr-1"></i>
                            <strong>Inactivo:</strong> No se requerirá identificación al cliente.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="row" style={{ alignItems: 'center', marginTop: '10px' }}>
                      <div className="col-md-6">
                        <Field
                          name="useRedsysInsite"
                          component={ToggleSwitch}
                          label="Pago Insite (sin redirección a Cyberpac)"
                          onChange={(e, newValue) => this.setState({ useRedsysInsite: newValue })}
                        />
                      </div>
                      <div className="col-md-6">
                        {this.state.useRedsysInsite ? (
                          <div className="alert alert-success" style={{ fontSize: '12px', margin: 0 }}>
                            <i className="fas fa-credit-card mr-1"></i>
                            <strong>Activo:</strong> El pago se realizará integrado en la webapp (Redsys Insite).
                          </div>
                        ) : (
                          <div className="alert alert-warning" style={{ fontSize: '12px', margin: 0 }}>
                            <i className="fas fa-external-link-alt mr-1"></i>
                            <strong>Inactivo:</strong> El pago redirigirá al TPV virtual de Cyberpac.
                          </div>
                        )}
                      </div>
                    </div>
                  </Box>
                </div>
              )}

              {/* Header Configuration Tab */}
              {activeTab === "header" && (
                <div className="tab-pane active">
                  <Box title="Colores del Header" icon="paint-brush">
                    <Field
                      name="headerConfig.primaryColor"
                      component={renderColorField}
                      label="Color Primario (Inicio Gradiente)"
                    />
                    <Field
                      name="headerConfig.secondaryColor"
                      component={renderColorField}
                      label="Color Secundario (Fin Gradiente)"
                    />
                  </Box>
                  <Box title="Colores de Texto y Fondo" icon="font" className="mt-3">
                    <Field
                      name="headerConfig.backgroundColor"
                      component={renderColorField}
                      label="Color de Fondo"
                    />
                    <Field
                      name="headerConfig.textPrimary"
                      component={renderColorField}
                      label="Texto Primario"
                    />
                    <Field
                      name="headerConfig.textSecondary"
                      component={renderColorField}
                      label="Texto Secundario"
                    />
                  </Box>
                </div>
              )}

              {/* Rental Customization Tab */}
              {activeTab === "rental" && (
                <div className="tab-pane active">
                  <Box title="Personalización Pantallas Rental" icon="palette">
                    <Field
                      name="headerConfig.languageSelectorColor"
                      component={renderColorField}
                      label="Color Selector de Idioma"
                    />
                    <Field
                      name="headerConfig.buttonColor1"
                      component={renderColorField}
                      label="Color Botón 1"
                    />
                    <Field
                      name="headerConfig.buttonColor2"
                      component={renderColorField}
                      label="Color Botón 2"
                    />
                    <Field
                      name="headerConfig.buttonText"
                      component={renderColorField}
                      label="Color Texto Botones"
                    />
                    <Field
                      name="headerConfig.iconsColor"
                      component={renderColorField}
                      label="Color de Iconos"
                    />
                  </Box>
                </div>
              )}

              {/* Images Tab */}
              {activeTab === "images" && (
                <div className="tab-pane active">
                  <Box title="Logo" icon="image">
                    <div className="form-group">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={(e) => this.handleFileUpload(e)}
                      />
                      <small className="form-text text-muted">
                        Selecciona una imagen (máx. 2MB)
                      </small>
                    </div>
                    {logoBase64 && (
                      <div className="mt-2">
                        <label>Logo Actual:</label>
                        <div
                          className="text-center p-2"
                          style={{
                            backgroundColor: primaryColor,
                            borderRadius: "4px",
                          }}
                        >
                          <img
                            src={logoBase64}
                            alt="Logo Actual"
                            style={{ maxHeight: "80px", maxWidth: "100%" }}
                          />
                        </div>
                      </div>
                    )}
                  </Box>
                  <Box title="Imágenes de Fondo" icon="image" className="mt-3">
                    <Field
                      name="headerConfig.backgroundImage"
                      component={renderInputField}
                      label="URL de Imagen de Fondo Desktop"
                      placeholder="https://ejemplo.com/imagen-desktop.jpg"
                    />
                    <Field
                      name="headerConfig.backgroundSmartphoneImage"
                      component={renderInputField}
                      label="URL de Imagen de Fondo Smartphone"
                      placeholder="https://ejemplo.com/imagen-smartphone.jpg"
                    />
                  </Box>
                </div>
              )}

              {/* Time Configuration Tab */}
              {activeTab === "time" && (
                <div className="tab-pane active">
                  <Box title="Configuración de Tiempo" icon="clock">
                    <Field
                      name="timeConfig.courtesyTime"
                      component={renderInputField}
                      type="number"
                      label="Tiempo de cortesía (minutos)"
                      parse={value => value ? Number(value) : null}
                    />
                    <Field
                      name="timeConfig.maxSlots"
                      component={renderInputField}
                      type="number"
                      label="Máximo de franjas por reserva"
                      parse={value => value ? Number(value) : null}
                    />
                  </Box>
                  <Box title="Franjas de Tiempo (minutos)" icon="list-ol" className="mt-3">
                    <FieldArray
                      name="timeConfig.timeSlots"
                      component={renderTimeSlots}
                    />
                  </Box>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="col-12 col-lg-5">
            <Box title="Vista Previa Desktop" icon="desktop" className="mb-3">
              <DesktopPreview
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                backgroundColor={backgroundColor}
                textPrimary={textPrimary}
                textSecondary={textSecondary}
                logoBase64={logoBase64}
                backgroundImage={backgroundImage}
                languageSelectorColor={languageSelectorColor}
                buttonColor1={buttonColor1}
                buttonColor2={buttonColor2}
                buttonText={buttonText}
                iconsColor={iconsColor}
              />
            </Box>
            <Box title="Vista Previa Smartphone" icon="mobile">
              <SmartphonePreview
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                backgroundColor={backgroundColor}
                textPrimary={textPrimary}
                textSecondary={textSecondary}
                logoBase64={logoBase64}
                backgroundSmartphoneImage={backgroundSmartphoneImage}
                backgroundImage={backgroundImage}
                languageSelectorColor={languageSelectorColor}
                buttonColor1={buttonColor1}
                buttonColor2={buttonColor2}
                buttonText={buttonText}
                iconsColor={iconsColor}
              />
            </Box>
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3">{error}</div>}
      </form>
      </div>
    );
  }
}

QREditForm = reduxForm({
  form: "QREditForm",
  enableReinitialize: true,
  onSubmitSuccess: (result, dispatch, props) => {
    props.push(`/qrs/${result.response._id}`);
  },
})(QREditForm);

const selector = formValueSelector("QREditForm");

const mapStateToProps = (state) => {
  const qr = state.qrs.qr;

  return {
    qr: qr,
    customers: state.customers.customers,
    initialValues: qr,
    primaryColor:
      selector(state, "headerConfig.primaryColor") ||
      (qr && qr.headerConfig && qr.headerConfig.primaryColor) ||
      "#01315f",
    secondaryColor:
      selector(state, "headerConfig.secondaryColor") ||
      (qr && qr.headerConfig && qr.headerConfig.secondaryColor) ||
      "#067ddb",
    backgroundColor:
      selector(state, "headerConfig.backgroundColor") ||
      (qr && qr.headerConfig && qr.headerConfig.backgroundColor) ||
      "#f3f3f3",
    textPrimary:
      selector(state, "headerConfig.textPrimary") ||
      (qr && qr.headerConfig && qr.headerConfig.textPrimary) ||
      "#01315f",
    textSecondary:
      selector(state, "headerConfig.textSecondary") ||
      (qr && qr.headerConfig && qr.headerConfig.textSecondary) ||
      "#808080",
    logoBase64:
      selector(state, "headerConfig.logoBase64") ||
      (qr && qr.headerConfig && qr.headerConfig.logoBase64),
    backgroundImage:
      selector(state, "headerConfig.backgroundImage") ||
      (qr && qr.headerConfig && qr.headerConfig.backgroundImage),
    backgroundSmartphoneImage:
      selector(state, "headerConfig.backgroundSmartphoneImage") ||
      (qr && qr.headerConfig && qr.headerConfig.backgroundSmartphoneImage),
    languageSelectorColor:
      selector(state, "headerConfig.languageSelectorColor") ||
      (qr && qr.headerConfig && qr.headerConfig.languageSelectorColor) ||
      "#ffffff",
    buttonColor1:
      selector(state, "headerConfig.buttonColor1") ||
      (qr && qr.headerConfig && qr.headerConfig.buttonColor1) ||
      "#01315f",
    buttonColor2:
      selector(state, "headerConfig.buttonColor2") ||
      (qr && qr.headerConfig && qr.headerConfig.buttonColor2) ||
      "#067ddb",
    buttonText:
      selector(state, "headerConfig.buttonText") ||
      (qr && qr.headerConfig && qr.headerConfig.buttonText) ||
      "#ffffff",
    iconsColor:
      selector(state, "headerConfig.iconsColor") ||
      (qr && qr.headerConfig && qr.headerConfig.iconsColor) ||
      "#01315f",
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchQR: bindActionCreators(fetchQR, dispatch),
  updateQR: bindActionCreators(updateQR, dispatch),
  fetchCustomers: bindActionCreators(fetchCustomers, dispatch),
  push: bindActionCreators(push, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(QREditForm);
